<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES, VOICE_CALL_STATUS } from '../constants';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
// Chatwoot's own audio chip — playback, scrubber, counter, speed, download. Nothing to build.
import Audio from 'next/message/chips/Audio.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const LABEL_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS',
  [VOICE_CALL_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
};

// COMPLETED must be mapped: without it the subtext fell through to the direction-aware default and
// a FINISHED call read "עדיין לא נענה". It must NOT repeat CALL_ENDED either, which LABEL_MAP
// already shows as the title — so a completed call says who answered, which is the useful detail.
const SUBTEXT_MAP = {
  [VOICE_CALL_STATUS.RINGING]: 'CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET',
};

const COMPLETED_SUBTEXT = {
  outbound: 'CONVERSATION.VOICE_CALL.THEY_ANSWERED',
  inbound: 'CONVERSATION.VOICE_CALL.YOU_ANSWERED',
};

const ICON_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'i-ph-phone-call',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.FAILED]: 'i-ph-phone-x',
};

const BG_COLOR_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'bg-n-teal-9',
  [VOICE_CALL_STATUS.RINGING]: 'bg-n-teal-9 animate-pulse',
  [VOICE_CALL_STATUS.COMPLETED]: 'bg-n-slate-11',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'bg-n-ruby-9',
  [VOICE_CALL_STATUS.FAILED]: 'bg-n-ruby-9',
};

const { contentAttributes, messageType, conversationId } = useMessageContext();
const { accountId } = useAccount();

const data = computed(() => contentAttributes.value?.data);
const status = computed(() => data.value?.status?.toString());

// SMARTICKS: the call recording, played inside the existing bubble rather than as a new component.
//
// The player is offered only when the PBX told us a file exists for THIS call. That flag is
// per-message on purpose: a merged thread holds several calls, and the conversation-level filename
// is only ever the newest one.
//
// Permission stays "may I hear this = may I see this conversation": the url is minted by an
// authorised API call, never guessable from the page.
// 🔴 camelCase, not snake_case. MessageList runs useCamelCase(messages, { deep: true }) over every
// message before rendering — only content_attributes.translations is excluded — so the API's
// recording_file and call_sid arrive here as recordingFile and callSid. Reading the snake_case form
// yields undefined, the player never renders, and nothing anywhere reports an error. The rest of
// this component never noticed because the only key it reads, `status`, is a single word.
// The snake_case fallbacks are for render paths that do not go through MessageList.
const recordingFile = computed(
  () => data.value?.recordingFile ?? data.value?.recording_file
);
const callSid = computed(() => data.value?.callSid ?? data.value?.call_sid);
const hasRecording = computed(() => Boolean(recordingFile.value));
// The playable url is fetched over axios rather than pointed at directly, for two reasons that
// each independently rule out the obvious approach:
//   * the API authenticates on the api_access_token HEADER (no session-cookie fallback), and an
//     <audio><source src> request sends no headers — a direct src would simply 401;
//   * Audio.vue passes its source through timeStampAppendedURL, which calls `new URL(src)` with no
//     base. A relative path THROWS there ("Failed to construct 'URL'") and takes the whole
//     component down mid-render, which is why the bubble first appeared with no player at all.
// The url Rails returns is absolute and already carries a signed `t`, so the helper leaves it be.
const playableUrl = ref(null);

const loadRecording = async () => {
  if (!hasRecording.value || !accountId.value) return;
  try {
    // window.axios, NOT `import axios from 'axios'`. entrypoints/dashboard.js builds the
    // authenticated instance (createAxios attaches access-token/client/uid from the auth
    // cookie) and assigns it to window.axios. A fresh import is the bare library with no
    // headers, so every call 401s — which is exactly what happened here.
    const { data: payload } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/smarticks/voice/recording`,
      {
        params: {
          conversation_id: conversationId.value,
          call_sid: callSid.value,
        },
      }
    );
    playableUrl.value = payload.url;
  } catch (e) {
    // A recording that cannot be fetched shows as no recording. The call record itself is the
    // point of this bubble and must never be lost to a storage problem.
    playableUrl.value = null;
  }
};

onMounted(loadRecording);

const recordingAttachment = computed(() => ({ dataUrl: playableUrl.value }));

const isOutbound = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isFailed = computed(() =>
  [VOICE_CALL_STATUS.NO_ANSWER, VOICE_CALL_STATUS.FAILED].includes(status.value)
);

const labelKey = computed(() => {
  if (LABEL_MAP[status.value]) return LABEL_MAP[status.value];
  if (status.value === VOICE_CALL_STATUS.RINGING) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.OUTGOING_CALL'
      : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
  }
  return isFailed.value
    ? 'CONVERSATION.VOICE_CALL.MISSED_CALL'
    : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
});

const subtextKey = computed(() => {
  if (SUBTEXT_MAP[status.value]) return SUBTEXT_MAP[status.value];
  if (status.value === VOICE_CALL_STATUS.COMPLETED) {
    return isOutbound.value
      ? COMPLETED_SUBTEXT.outbound
      : COMPLETED_SUBTEXT.inbound;
  }
  if (status.value === VOICE_CALL_STATUS.IN_PROGRESS) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.THEY_ANSWERED'
      : 'CONVERSATION.VOICE_CALL.YOU_ANSWERED';
  }
  return isFailed.value
    ? 'CONVERSATION.VOICE_CALL.NO_ANSWER'
    : 'CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET';
});

const iconName = computed(() => {
  if (ICON_MAP[status.value]) return ICON_MAP[status.value];
  return isOutbound.value ? 'i-ph-phone-outgoing' : 'i-ph-phone-incoming';
});

const bgColor = computed(() => BG_COLOR_MAP[status.value] || 'bg-n-teal-9');
</script>

<template>
  <BaseBubble class="p-0 border-none">
    <div class="flex overflow-hidden flex-col w-full max-w-xs">
      <div class="flex gap-3 items-center p-3 w-full">
        <div
          class="flex justify-center items-center rounded-full size-10 shrink-0"
          :class="bgColor"
        >
          <Icon
            class="size-5"
            :icon="iconName"
            :class="{
              'text-n-slate-1': status === VOICE_CALL_STATUS.COMPLETED,
              'text-white': status !== VOICE_CALL_STATUS.COMPLETED,
            }"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ $t(labelKey) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t(subtextKey) }}
          </span>
        </div>
      </div>

      <!-- SMARTICKS: only when this call actually has audio. An empty recording (a 44-byte WAV
           header from a call nobody answered) is filtered server-side, so no player appears
           rather than one showing 00:00 that cannot play. -->
      <div v-if="hasRecording && playableUrl" class="px-3 pb-3 -mt-1">
        <Audio
          :attachment="recordingAttachment"
          :show-transcribed-text="false"
        />
      </div>
    </div>
  </BaseBubble>
</template>
